import { DragEvent, FormEvent, useRef, useState } from "react"
import { router, useForm, usePage } from "@inertiajs/react"
import { ImagePlus, Images, Trash2, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

export type PhotoManagerProps = {
  photographable_type: "Destination" | "Place" | "Trail"
  photographable_id: number
  photos: Array<{
    id: number
    alt_text_en: string | null
    alt_text_de: string | null
    credit: string | null
    position: number
    primary: boolean
    preview_url: string
  }>
}

type UploadData = {
  photographable_type: string
  photographable_id: string
  images: File[]
}

export function PhotoManager({ photoManager }: { photoManager: PhotoManagerProps }) {
  const { props } = usePage<PageProps>()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const upload = useForm<UploadData>({
    photographable_type: photoManager.photographable_type,
    photographable_id: photoManager.photographable_id.toString(),
    images: [],
  })

  const chooseFiles = (files: FileList | null) => {
    upload.setData("images", files ? Array.from(files) : [])
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    chooseFiles(event.dataTransfer.files)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    upload.post("/admin/photos", { forceFormData: true, preserveScroll: true })
  }

  return (
    <section className="mt-8 border-t border-hairline pt-7">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-faded text-accent-display">
          <Images className="h-5 w-5" aria-hidden />
        </span>
        <div>
          <h2>Editorial photos</h2>
          <p className="mt-1">Upload clean editorial images, then add bilingual alt text, credit, order, and a primary selection.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="sr-only"
          onChange={(event) => chooseFiles(event.target.files)}
        />
        <div
          className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-accent bg-accent-faded" : "border-hairline bg-surface"}`}
          onDragEnter={(event) => { event.preventDefault(); setDragging(true) }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <ImagePlus className="mx-auto h-7 w-7 text-ink-muted" aria-hidden />
          <p className="mt-3">Drag JPEG, PNG, or WebP photos here</p>
          <Button type="button" variant="secondary" className="mt-3" onClick={() => inputRef.current?.click()}>
            Choose photos
          </Button>
          {upload.data.images.length > 0 && (
            <p className="mt-3 text-sm text-ink-muted">{upload.data.images.length} photo{upload.data.images.length === 1 ? "" : "s"} ready</p>
          )}
        </div>
        {props.errors?.images && <p className="mt-2 text-sm text-danger">{props.errors.images}</p>}
        <Button type="submit" className="mt-3 w-full sm:w-auto" disabled={upload.processing || upload.data.images.length === 0}>
          <Upload className="h-4 w-4" aria-hidden />
          {upload.processing ? "Sanitizing and uploading..." : "Upload photos"}
        </Button>
      </form>

      {photoManager.photos.length > 0 && (
        <div className="mt-7 space-y-4">
          {photoManager.photos.map((photo) => <PhotoEditor key={photo.id} photo={photo} />)}
        </div>
      )}
    </section>
  )
}

function PhotoEditor({ photo }: { photo: PhotoManagerProps["photos"][number] }) {
  const form = useForm({
    alt_text_en: photo.alt_text_en ?? "",
    alt_text_de: photo.alt_text_de ?? "",
    credit: photo.credit ?? "",
    position: photo.position.toString(),
    primary: photo.primary,
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.patch(`/admin/photos/${photo.id}`, { preserveScroll: true })
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-xl border border-hairline bg-surface p-4 shadow-sm md:grid-cols-[12rem_1fr]">
      <div>
        <img src={photo.preview_url} alt="" className="aspect-[3/2] w-full rounded-lg object-cover" />
        <label className="mt-3 flex items-center gap-2 font-normal text-ink-body">
          <input type="checkbox" checked={form.data.primary} onChange={(event) => form.setData("primary", event.target.checked)} />
          Primary photo
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="English alt text"><Input value={form.data.alt_text_en} onChange={(event) => form.setData("alt_text_en", event.target.value)} /></Field>
        <Field label="German alt text"><Input value={form.data.alt_text_de} onChange={(event) => form.setData("alt_text_de", event.target.value)} /></Field>
        <Field label="Credit"><Input value={form.data.credit} onChange={(event) => form.setData("credit", event.target.value)} /></Field>
        <Field label="Order"><Input type="number" min="0" value={form.data.position} onChange={(event) => form.setData("position", event.target.value)} /></Field>
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
          <Button type="submit" disabled={form.processing}>Save photo</Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => router.delete(`/admin/photos/${photo.id}`, { preserveScroll: true })}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Remove
          </Button>
        </div>
      </div>
    </form>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label>{label}</label><div className="mt-1.5">{children}</div></div>
}
